import { PotentialOffer } from '../../../types';

export interface PriceDisplay {
  price: string;
  type: string;
  targetPrice?: number;
  margin?: number;
  marginColor?: 'red' | 'green';
}

// ========================================
// LABEL CONSTANTS
// ========================================
const LABELS = {
  FAIR_PRICE: 'Fair Price',
  ESTIMATED_PRICE: 'Estimated Price',
  CLIENT_PRICE: 'Client Price',
  TARGET_PRICE: 'Target Price',
  ACCEPTED_PRICE: 'Accepted Price',
  MARGIN: 'Margin',
} as const;

// ========================================
// HELPER FUNCTIONS - PRICE CALCULATION
// ========================================

/**
 * Get the most recent negotiation from an offer
 */
const getMostRecentNegotiation = (offer: PotentialOffer) => {
  if (!offer.negotiations?.length) return null;
  
  return [...offer.negotiations]
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    })[0];
};

/**
 * Get fair price with fallback logic
 * Priority: mostRecentNegotiation.fairPrice → offer.totalPrice → offer.estimatedPrice
 */
const getFairPrice = (offer: PotentialOffer): number | undefined => {
  const mostRecent = getMostRecentNegotiation(offer);
  
  if (mostRecent?.fairPrice) {
    return mostRecent.fairPrice;
  }
  
  return offer.totalPrice || offer.estimatedPrice;
};

/**
 * Calculate margin percentage between accepted price and fair price
 * Returns: { percent: number, color: 'green' | 'red' }
 */
const calculateMargin = (acceptedPrice: number, fairPrice: number) => {
  const percent = ((acceptedPrice - fairPrice) / fairPrice) * 100;
  const roundedPercent = Math.round(percent * 10) / 10;
  
  return {
    percent: roundedPercent,
    color: (percent > 0 ? 'green' : 'red') as 'green' | 'red',
  };
};

/**
 * Round price to nearest integer
 */
const roundPrice = (price: number): number => Math.round(price);

/**
 * Format margin percentage with +/- sign
 */
const formatMarginPercent = (percent: number): string => {
  const sign = percent > 0 ? '+' : '';
  return `${sign}${percent}%`;
};

// ========================================
// HELPER FUNCTIONS - LABEL GENERATION
// ========================================

/**
 * Determine base price type: Fair Price or Estimated Price
 */
const getBasePriceType = (offer: PotentialOffer): string => (
  offer.totalPrice ? LABELS.FAIR_PRICE : LABELS.ESTIMATED_PRICE
);

/**
 * Build composite label from parts
 */
const buildLabel = (...parts: string[]): string => parts.join(' / ');

/**
 * Get label for accepted price display
 */
const getAcceptedPriceLabel = (offer: PotentialOffer): string => {
  const basePriceType = getBasePriceType(offer);
  return buildLabel(LABELS.ACCEPTED_PRICE, basePriceType, LABELS.MARGIN);
};

/**
 * Get label for price with target
 */
const getPriceWithTargetLabel = (offer: PotentialOffer): string => {
  const basePriceType = getBasePriceType(offer);
  return buildLabel(basePriceType, LABELS.TARGET_PRICE);
};

// ========================================
// DISPLAY PRICE GENERATORS
// ========================================

/**
 * Generate display for accepted price scenario
 * Shows: Accepted Price / Fair Price / Margin %
 */
const getAcceptedPriceDisplay = (offer: PotentialOffer): PriceDisplay => {
  const fairPrice = getFairPrice(offer) || offer.estimatedPrice!;
  const { percent, color } = calculateMargin(offer.acceptedPrice!, fairPrice);
  
  const priceText = [
    roundPrice(offer.acceptedPrice!),
    roundPrice(fairPrice),
    formatMarginPercent(percent),
  ].join(' / ');
  
  return {
    price: priceText,
    type: getAcceptedPriceLabel(offer),
    targetPrice: roundPrice(fairPrice),
    margin: percent,
    marginColor: color,
  };
};

/**
 * Generate display for client price scenario
 * Shows: Client Price / Fair Price
 */
const getClientPriceDisplay = (offer: PotentialOffer): PriceDisplay => {
  const fairPrice = getFairPrice(offer) || offer.estimatedPrice!;
  
  return {
    price: `${roundPrice(offer.fePrice!)} / ${roundPrice(fairPrice)}`,
    targetPrice: roundPrice(fairPrice),
    type: buildLabel(LABELS.CLIENT_PRICE, LABELS.FAIR_PRICE),
  };
};

/**
 * Generate display for ongoing negotiation scenario
 * Shows: Fair Price / Target Price (or Estimated Price / Target Price)
 */
const getNegotiationPriceDisplay = (offer: PotentialOffer): PriceDisplay => {
  const mostRecent = getMostRecentNegotiation(offer)!;
  const fairPrice = getFairPrice(offer);
  const targetPrice = mostRecent.targetPrice || offer.estimatedPrice || offer.fePrice || 0;
  
  // Determine label based on whether client provided their price
  const label = offer.fePrice 
    ? buildLabel(LABELS.FAIR_PRICE, LABELS.TARGET_PRICE)
    : getPriceWithTargetLabel(offer);
  
  // Determine which price to display
  const displayPrice = fairPrice || mostRecent.targetPrice || offer.estimatedPrice || 0;
  
  return {
    price: `${roundPrice(displayPrice)} / ${roundPrice(targetPrice)}`,
    targetPrice: roundPrice(targetPrice),
    type: label,
  };
};

/**
 * Generate display for simple client price scenario
 * Shows: Client Price
 */
const getSimpleClientPriceDisplay = (offer: PotentialOffer): PriceDisplay => {
  const price = roundPrice(offer.fePrice!);
  
  return {
    price: price.toString(),
    type: LABELS.CLIENT_PRICE,
    targetPrice: price,
  };
};

/**
 * Generate display for estimated price with fair price scenario
 * Shows: Estimated Price / Fair Price
 */
const getEstimatedWithFairPriceDisplay = (offer: PotentialOffer): PriceDisplay => ({
  price: `${roundPrice(offer.estimatedPrice!)} / ${roundPrice(offer.totalPrice!)}`,
  targetPrice: roundPrice(offer.totalPrice!),
  type: buildLabel(LABELS.ESTIMATED_PRICE, LABELS.FAIR_PRICE),
});

/**
 * Generate display for simple estimated price scenario
 * Shows: Estimated Price
 */
const getSimpleEstimatedPriceDisplay = (offer: PotentialOffer): PriceDisplay => {
  const price = roundPrice(offer.estimatedPrice!);
  
  return {
    price: price.toString(),
    targetPrice: price,
    type: LABELS.ESTIMATED_PRICE,
  };
};

// ========================================
// MAIN EXPORT
// ========================================

/**
 * Get the appropriate price display based on offer state
 * 
 * Priority order:
 * 1. Accepted price (deal closed)
 * 2. Client price with estimated price (client has provided their budget)
 * 3. Active negotiations (ongoing negotiation)
 * 4. Simple client price (client budget only)
 * 5. Estimated price with fair price (system has both estimates)
 * 6. Simple estimated price (only system estimate)
 * 7. No price available
 */
export const getDisplayPrice = (offer: PotentialOffer): PriceDisplay => {
  // 1. Deal is closed - show accepted price with margin
  if (offer.acceptedPrice && offer.estimatedPrice) {
    return getAcceptedPriceDisplay(offer);
  }

  // 2. Client has provided budget and we have estimates - compare them
  if (offer.fePrice && offer.estimatedPrice) {
    return getClientPriceDisplay(offer);
  }

  // 3. Ongoing negotiation - show current negotiation prices
  if (offer.negotiations?.length) {
    return getNegotiationPriceDisplay(offer);
  }
  
  // 4. Only client price available
  if (offer.fePrice) {
    return getSimpleClientPriceDisplay(offer);
  }
  
  // 5. Estimated price with computed fair price
  if (offer.estimatedPrice && offer.totalPrice) {
    return getEstimatedWithFairPriceDisplay(offer);
  }
  
  // 6. Only estimated price available
  if (offer.estimatedPrice) {
    return getSimpleEstimatedPriceDisplay(offer);
  }
  
  // 7. No price information available
  return { price: 'N/A', type: 'N/A' };
};
